<template>
  <div class="space-y-8 max-h-[80vh] min-h-78" data-cy="stips-v2">
    <loader :is-loading />
    <div class="flex items-center justify-between">
      <div>
        <search-input v-model="searchTerm" no-padding no-margin />
      </div>
      <div>
        <div class="relative">
          <lf-button
            v-if="canCreateStipDefinitions"
            class="bg-primary/10 text-primary mr-4"
            data-cy="createNewStipButton"
            @click="showStipCreator = true"
          >
            {{ $t("DEALS.DOCUMENTS.ADD_NEW_DOCUMENT") }}
          </lf-button>
          <div v-if="showStipCreator" class="absolute right-5 top-[115%]">
            <stips-v2-stip-creator @close="showStipCreator = false" />
          </div>
        </div>
      </div>
    </div>
    <div
      ref="stipsContainer"
      class="grid grid-cols-3 gap-4 overflow-y-auto max-h-[75vh]"
      data-cy="stip-list"
    >
      <stip-v2
        v-for="stipDefinition in stipDefinitions"
        :key="stipDefinition.id"
        :disabled="readonly"
        :select-only
        :stip-definition
        :stip="getFieldStipForStipDefinition(stipDefinition)"
        @toggle="toggleStip(stipDefinition)"
        @update="(newValues) => updateSelectedStip(newValues)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useInfiniteScroll } from "@vueuse/core";
import { computed, onMounted, ref, useTemplateRef } from "vue";

import { useAuth } from "@/hooks/auth";
import { useFilters } from "@/hooks/filters";
import { useStipsInfiniteQuery } from "@/hooks/queries/stips";
import { toFieldStip } from "@/models/stips";

import Loader from "@/components/ui/Loader.vue";
import LfButton from "@/components/ui/buttons/LfButton.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import StipV2 from "@/views/deals/components/StipV2.vue";
import StipsV2StipCreator from "@/views/deals/components/StipsV2StipCreator.vue";

import type { DealStip, FieldStip, StipDefinition } from "@/models/stips";

const { readonly = false, selectOnly = false } = defineProps<{
  readonly?: boolean;
  selectOnly?: boolean;
}>();

const selectedStipsModel = defineModel<(FieldStip | DealStip)[]>({
  required: true,
  default: [],
});

const stipsContainer = useTemplateRef("stipsContainer");

const { filterStateAsSearchParams, searchTerm } = useFilters();

const { isAdmin, isClientAdmin, isClientUnderwriter } = useAuth();

const {
  data: stipsResponseData,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
} = useStipsInfiniteQuery(filterStateAsSearchParams);

useInfiniteScroll(
  stipsContainer,
  async () => {
    await fetchNextPage();
  },
  {
    distance: 50,
    canLoadMore: () => !isLoading.value && !isFetchingNextPage.value,
  }
);

const showStipCreator = ref(false);

const canCreateStipDefinitions = computed(() => {
  return (isAdmin || isClientAdmin || isClientUnderwriter) && !readonly;
});

const stipDefinitions = computed<StipDefinition[]>(() => {
  return stipsResponseData.value?.pages.flatMap((page) => page.data) ?? [];
});

/**
 * Read and write stips through this computed rather than `selectedStipsModel`.
 * This ensures stips will always be normalized to field stips when editing or creating stips.
 *
 * For these stips, `id` will always mean "id of the reference stip definition".
 */
const selectedStips = computed<FieldStip[]>({
  get() {
    return selectedStipsModel.value.map(toFieldStip);
  },
  set(newValue) {
    selectedStipsModel.value = newValue;
  },
});

const toggleStip = (stipDefinition: StipDefinition) => {
  if (stipIsSelected(stipDefinition)) {
    selectedStips.value = selectedStips.value.filter(
      (stip) => stip.id !== stipDefinition.id
    );
  } else {
    selectedStips.value = [
      ...selectedStips.value,
      {
        id: stipDefinition.id,
        description: stipDefinition.description,
        count: 1,
        is_optional: false,
      },
    ];
  }
};

const updateSelectedStip = (updatedStip: FieldStip) => {
  selectedStips.value = selectedStips.value.map((selectedStip) =>
    updatedStip.id === selectedStip.id ? updatedStip : selectedStip
  );
};

const stipIsSelected = (stipDefinition: StipDefinition) => {
  return selectedStips.value.some(
    (selectedStip) => selectedStip.id === stipDefinition.id
  );
};

const getFieldStipForStipDefinition = (stipDefintion: StipDefinition) => {
  return selectedStips.value.find(
    (selectedStip) => selectedStip.id === stipDefintion.id
  );
};

onMounted(() => {
  selectedStipsModel.value = selectedStips.value.map(toFieldStip);
});
</script>
